import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, User } from "lucide-react";
import { toast } from "sonner";
import SmartPhraseContentEditor from "@/components/smartphrase/SmartPhraseContentEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { SP } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { canWriteMainTab } from "@/lib/maintabAccess";
import {
  countWordsAndChars,
  createSmartPhrase,
  getSmartPhrase,
  SMART_PHRASE_FORMATS,
  smartPhrasesPath,
  updateSmartPhrase,
} from "@/lib/smartPhraseApi";

function emptyForm() {
  return {
    name: "",
    description: "",
    format: "richtext",
    active: true,
    content: "",
  };
}

function Field({ label, required, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">
        {label}
        {required ? <span className="text-rose-600"> *</span> : null}
      </Label>
      {children}
      {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}

export default function SmartPhraseFormPage() {
  const { phraseId } = useParams();
  const isEdit = Boolean(phraseId);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = canWriteMainTab(user?.maintabAccess, "smartphrases");
  const [form, setForm] = useState(emptyForm);
  const [ownerDisplay, setOwnerDisplay] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) {
      setOwnerDisplay(user?.displayName ? `${user.displayName} (You)` : "You");
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const phrase = await getSmartPhrase(phraseId);
        if (cancelled) return;
        setForm({
          name: phrase.name || "",
          description: phrase.description || "",
          format: phrase.format === "plaintext" ? "plaintext" : "richtext",
          active: phrase.active !== false,
          content: phrase.content || "",
        });
        const mine = phrase.ownerUserId === user?.id;
        setOwnerDisplay(
          mine
            ? `${phrase.ownerDisplay || user?.displayName || "You"} (You)`
            : phrase.ownerDisplay || "—",
        );
      } catch {
        if (!cancelled) {
          toast.error("Could not load SmartPhrase.");
          navigate(smartPhrasesPath());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, navigate, phraseId, user]);

  const counts = useMemo(
    () => countWordsAndChars(form.content, form.format),
    [form.content, form.format],
  );

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const validate = () => {
    if (!form.name.trim()) return "Name (Shortcut) is required.";
    if (!form.description.trim()) return "Description is required.";
    const { characters } = countWordsAndChars(form.content, form.format);
    if (!characters) return "SmartPhrase content is required.";
    return null;
  };

  const onSave = async () => {
    if (!canWrite) return;
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        content: form.content,
        format: form.format,
        active: form.active,
      };
      if (isEdit) {
        await updateSmartPhrase(phraseId, payload);
        toast.success("SmartPhrase updated.");
      } else {
        await createSmartPhrase(payload);
        toast.success("SmartPhrase saved.");
      }
      navigate(smartPhrasesPath());
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join("; ") : detail;
      toast.error(msg || "Failed to save SmartPhrase.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div data-testid={SP.formPage} className="p-10 text-center text-sm text-slate-400">
        Loading SmartPhrase…
      </div>
    );
  }

  return (
    <div data-testid={SP.formPage} className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Link
            to={smartPhrasesPath()}
            data-testid={SP.backLink}
            className="mb-2 inline-flex items-center gap-1 text-sm font-medium text-teal-800 hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to SmartPhrase List
          </Link>
          <h1 data-testid={SP.formTitle} className="text-page-title font-semibold tracking-tight text-slate-900">
            {isEdit ? "Edit SmartPhrase" : "Create SmartPhrase"}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Reusable documentation template. Saving does not change Notes or Encounters.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            data-testid={SP.cancelBtn}
            onClick={() => navigate(smartPhrasesPath())}
            disabled={saving}
          >
            Cancel
          </Button>
          {canWrite ? (
          <Button
            data-testid={SP.saveBtn}
            className="bg-teal-700 hover:bg-teal-800"
            onClick={onSave}
            disabled={saving}
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Save SmartPhrase"}
          </Button>
          ) : null}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="space-y-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            SmartPhrase details
          </h2>
          <Field
            label="Name (Shortcut)"
            required
            hint="Used to quickly find and insert this SmartPhrase."
          >
            <Input
              data-testid={SP.nameInput}
              value={form.name}
              onChange={(event) => setField("name", event.target.value)}
              placeholder="e.g. DRTLESI"
              className="h-10"
            />
          </Field>
          <Field label="Description" required>
            <Textarea
              data-testid={SP.descriptionInput}
              value={form.description}
              onChange={(event) => setField("description", event.target.value)}
              placeholder="e.g. Lumbar epidural steroid injection procedure note"
              className="min-h-[96px]"
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Format" required>
              <Select value={form.format} onValueChange={(value) => setField("format", value)}>
                <SelectTrigger data-testid={SP.formatSelect} className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SMART_PHRASE_FORMATS.map((option) => (
                    <SelectItem key={option.code} value={option.code}>
                      {option.display}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select
                value={form.active ? "active" : "inactive"}
                onValueChange={(value) => setField("active", value === "active")}
              >
                <SelectTrigger data-testid={SP.statusSelect} className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Owner">
            <div
              data-testid={SP.ownerDisplay}
              className="flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700"
            >
              <User className="h-4 w-4 text-slate-400" />
              {ownerDisplay || "—"}
            </div>
          </Field>
        </section>

        <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            SmartPhrase content <span className="text-rose-600">*</span>
          </h2>
          <SmartPhraseContentEditor
            format={form.format}
            value={form.content}
            onChange={(value) => setField("content", value)}
          />
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3 text-xs text-slate-500">
            <p>
              This content will be inserted into your note when you use this SmartPhrase. You can
              edit the inserted content in the note after insertion.
            </p>
            <div className="shrink-0 font-medium text-slate-600">
              <span data-testid={SP.wordCount}>Words: {counts.words}</span>
              <span className="mx-2 text-slate-300">·</span>
              <span data-testid={SP.charCount}>Characters: {counts.characters}</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
